import { T, useT } from '@transifex/react';
import { type ReactNode } from 'react';
import { type Control } from 'react-hook-form';
import { Platform, Text as RNText, type TextProps } from 'react-native';
import { type z } from 'zod';

import { type LegalDocument } from '@/modules/api';
import { type LegalDocumentGroup } from '@/modules/legalDocuments';
import { CheckboxField } from '@/modules/ui';

import { type accountSchema } from '../schema/accountSchema';
import { openPolicy } from '../utils/openPolicy';

type FormValues = z.infer<typeof accountSchema>;

type LegalDocumentCheckboxProps = {
  control: Control<FormValues>;
  group: LegalDocumentGroup;
};

// One checkbox per LegalDocumentGroup. The anchor's name labels the
// checkbox; linked docs in the same `checkboxGroup` are inline links inside
// the label. RHF field path is `agreements.<anchor.uuid>` — schema validates
// the record requires every value to be `true`.
export function LegalDocumentCheckbox({ control, group }: LegalDocumentCheckboxProps) {
  const t = useT();
  const { anchor, linked } = group;
  const allNames = [anchor, ...linked].map((d) => d.name).join(', ');

  return (
    <CheckboxField
      control={control}
      name={`agreements.${anchor.uuid}`}
      size='sm'
      accessibilityLabel={t('Agree to {documents}', { documents: allNames })}
      testID={`account-creation-legal-${anchor.key}`}
    >
      {renderLabel(anchor, linked)}
    </CheckboxField>
  );
}

// Per-count templates: Transifex `<T>` stringifies array placeholders via
// `Array.prototype.toString()`, so we can't pass a join of N links as one
// placeholder. Each link gets its own named slot. The API today caps at 2
// linked docs per group; if that ever grows, add a new template here.
function renderLabel(anchor: LegalDocument, linked: LegalDocument[]): ReactNode {
  const anchorLink = <PolicyLink doc={anchor} />;
  if (linked.length === 0) {
    return <T _str='I have read and agree to the {anchor}.' anchor={anchorLink} />;
  }
  if (linked.length === 1) {
    return (
      <T
        _str='I have read and agree to the {anchor} and have read and acknowledge the {link1}.'
        anchor={anchorLink}
        link1={<PolicyLink doc={linked[0]} />}
      />
    );
  }
  // 2 linked — the Terms-of-Use group today: anchor + Privacy + Notice.
  return (
    <T
      _str='I have read and agree to the {anchor} and have read and acknowledge the {link1} & {link2}.'
      anchor={anchorLink}
      link1={<PolicyLink doc={linked[0]} />}
      link2={<PolicyLink doc={linked[1]} />}
    />
  );
}

// Bare RN Text (not vida-ui's wrapped Text). cssInterop on the wrapped Text
// drops onPress when it's nested inside another Text on web; bare RN Text
// keeps the press attachment on both web and native.
//
// Web renders a REAL anchor (react-native-web maps `href` on Text to `<a>`)
// instead of an onPress + window.open: `window.open` goes through WKWebView's
// UI delegate (`createWebViewWith`), which the native shells don't implement,
// so policy links silently did nothing in the embedded native WebView. A real
// `target=_blank` anchor goes through the navigation delegate, which the native
// app does handle (and desktop web opens a normal new tab). Clicking an anchor
// inside the checkbox `<label>` doesn't toggle the checkbox — label activation
// skips interactive descendants — so no preventDefault dance is needed either.
// `href`/`hrefAttrs` are RN-web extensions, hence the cast.
function PolicyLink({ doc }: { doc: LegalDocument }) {
  const linkProps: TextProps =
    Platform.OS === 'web'
      ? ({
          href: doc.documentUrl,
          hrefAttrs: { target: '_blank', rel: 'noopener noreferrer' },
        } as TextProps)
      : { onPress: openPolicy(doc.documentUrl) };
  return (
    <RNText
      {...linkProps}
      testID={`account-creation-policy-${doc.key}`}
      className='text-sm text-primary-500 underline hover:no-underline'
      role='link'
    >
      {doc.name}
    </RNText>
  );
}
